import { env } from '@/config/env';
import { supabase } from '@/lib/supabaseClient';

/**
 * Привязка площадок к одному аккаунту (ADR-046).
 *
 * Клиент только передаёт код: кто чистый, кто кого поглощает и сколько
 * попыток осталось — решает сервер (supabase/functions/account-link).
 * Итог для человека строится по ответу confirm, а не по предпросмотру:
 * между ними состояние аккаунтов могло измениться.
 */

export type LinkPlatform = 'telegram' | 'vk' | 'ok';

export interface LinkCode {
  code: string;
  /** Длительность по часам сервера — часы телефона могут расходиться. */
  expiresInSeconds: number;
}

export interface LinkPreview {
  survivorPlatform: LinkPlatform;
  absorbedPlatform: LinkPlatform;
  burnedCredits: number;
}

export class AccountLinkError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'AccountLinkError';
    this.code = code;
  }
}

const PLATFORMS: readonly LinkPlatform[] = ['telegram', 'vk', 'ok'];

function isLinkPlatform(value: unknown): value is LinkPlatform {
  return typeof value === 'string' && (PLATFORMS as readonly string[]).includes(value);
}

const UNEXPECTED = 'Сервер ответил неожиданно. Попробуйте ещё раз.';

async function call(accessToken: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/account-link`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AccountLinkError('Нет связи с сервером. Проверьте интернет.', 'NETWORK_ERROR');
  }

  const payload = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: { code?: string; message?: string } })
    | null;

  if (!payload) {
    throw new AccountLinkError(UNEXPECTED, 'UNKNOWN_ERROR');
  }

  if (!response.ok || payload.error) {
    const { message, code } = (payload.error ?? {}) as { message?: unknown; code?: unknown };
    throw new AccountLinkError(
      typeof message === 'string' ? message : 'Не удалось привязать аккаунт. Попробуйте ещё раз.',
      typeof code === 'string' ? code : 'UNKNOWN_ERROR',
    );
  }

  return payload;
}

function toPreview(payload: Record<string, unknown>): LinkPreview {
  if (
    !isLinkPlatform(payload.survivorPlatform) ||
    !isLinkPlatform(payload.absorbedPlatform) ||
    typeof payload.burnedCredits !== 'number'
  ) {
    throw new AccountLinkError(UNEXPECTED, 'UNKNOWN_ERROR');
  }

  return {
    survivorPlatform: payload.survivorPlatform,
    absorbedPlatform: payload.absorbedPlatform,
    burnedCredits: payload.burnedCredits,
  };
}

export async function createLinkCode(accessToken: string): Promise<LinkCode> {
  const payload = await call(accessToken, { action: 'create-code' });

  if (typeof payload.code !== 'string' || typeof payload.expiresInSeconds !== 'number') {
    throw new AccountLinkError(UNEXPECTED, 'UNKNOWN_ERROR');
  }

  return { code: payload.code, expiresInSeconds: payload.expiresInSeconds };
}

export async function previewLink(accessToken: string, code: string): Promise<LinkPreview> {
  return toPreview(await call(accessToken, { action: 'preview', code }));
}

export async function confirmLink(accessToken: string, code: string): Promise<LinkPreview> {
  return toPreview(await call(accessToken, { action: 'confirm', code }));
}

/** Свои площадки; RLS отдаёт только собственные строки. */
export async function fetchLinkedPlatforms(): Promise<LinkPlatform[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase.from('platform_accounts').select('platform').order('linked_at');

  if (error) {
    throw new Error('Не удалось загрузить связанные площадки.');
  }

  return (data ?? []).map((row) => row.platform).filter(isLinkPlatform);
}
